/**
 * ATL-953 — build the `constants/loyaltySettings` update, without ever emitting `NaN`.
 *
 * `parseInt()` returns `NaN` for an empty field, for `undefined`, and for junk text — and Firestore
 * STORES `NaN`: it is a valid IEEE-754 double, not a rejected value. Two production organizations
 * carry `punchCardCapacity: NaN` written by this screen (denominator: 382 migrated orgs, scanned
 * 2026-08-25).
 *
 * The damage is one-sided and permanent. The backend drops non-finite numbers on purpose
 * (`buildLoyaltySettingsRow` / `Number.isFinite`), so the dual-write leaves Supabase holding the
 * last good value while Firestore holds junk — a disagreement no backfill can ever close, because
 * the correct side is refusing to copy the incorrect one. The gate reported it as loyalty-settings
 * drift and was asking the write path for something the write path is right to refuse.
 *
 * Two rules, and they are not the same rule:
 *
 *   • A number an ENABLED feature depends on must be valid → REFUSE the save. Junk in the books is
 *     worse than an unsaved form.
 *   • A number belonging to a DISABLED feature → OMIT the key. `updateLoyaltySettings` issues a
 *     Firestore `.update()`, so an omitted key keeps whatever is stored. Coercing to `0` instead
 *     (`parseInt(x) || 0`) would silently REWRITE a real capacity to zero, and a zero capacity is
 *     not "unset" — `punchCardReduction` and `[...Array(capacity - 1)]` both read it.
 *
 * @param {{cashbackEnabled: boolean, defaultBonusLevel: unknown, punchCardEnabled: boolean, punchCardCapacity: unknown}} form
 * @returns {{info: Object} | {refusal: 'incomplete'}}
 */
export function buildLoyaltySettingsUpdate({
  cashbackEnabled,
  defaultBonusLevel,
  punchCardEnabled,
  punchCardCapacity,
}) {
  const finiteOrNull = (raw) => {
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : null
  }
  const bonusLevel = finiteOrNull(defaultBonusLevel)
  const capacity = finiteOrNull(punchCardCapacity)

  if ((cashbackEnabled && bonusLevel === null) || (punchCardEnabled && capacity === null)) {
    return { refusal: 'incomplete' }
  }
  // Pre-existing behaviour, kept verbatim: a blank cashback rate refuses the save even when
  // cashback is off. Relaxing it is a separate decision from this defect.
  if (defaultBonusLevel === '') return { refusal: 'incomplete' }

  return {
    info: {
      cashbackEnabled,
      punchCardEnabled,
      ...(bonusLevel === null ? {} : { defaultBonusLevel: bonusLevel }),
      ...(capacity === null ? {} : { punchCardCapacity: capacity }),
    },
  }
}

export default buildLoyaltySettingsUpdate
